// zoomUtils.js - Centralized zoom modifier system

// Get zoom factor based on screen width
export const getZoomFactor = () => {
  const width = window.innerWidth;
  
  if (width >= 3840) return 3.0;      // 4K+ screens
  if (width >= 2560) return 1.5;      // Large screens
  if (width >= 1920) return 1.2;      // High-res screens
  return 1.0;                         // Standard screens
};

// Apply zoom to a single value
export const zoom = (value) => {
  if (typeof value === 'string') {
    // Handle string values like "20px", "1.5em", etc.
    const match = value.match(/^(-?\d*\.?\d+)(.*)$/);
    if (match) {
      const [, number, unit] = match;
      return `${parseFloat(number) * getZoomFactor()}${unit}`;
    }
    return value;
  }
  
  if (typeof value === 'number') {
    return value * getZoomFactor();
  }
  
  return value;
};

// Apply zoom to multiple values (for shorthand properties)
export const zoomMultiple = (...values) => {
  return values.map(v => zoom(v)).join(' ');
};

// Apply zoom to an object of styles
export const zoomStyles = (styles) => {
  const zoomedStyles = {};
  
  for (const [key, value] of Object.entries(styles)) {
    if (Array.isArray(value)) {
      // Handle arrays (like for multiple values)
      zoomedStyles[key] = value.map(v => zoom(v));
    } else if (typeof value === 'object' && value !== null) {
      // Recursively handle nested objects
      zoomedStyles[key] = zoomStyles(value);
    } else {
      // Handle single values
      zoomedStyles[key] = zoom(value);
    }
  }
  
  return zoomedStyles;
};

// Specific zoom functions for common properties
export const zoomPadding = (top, right = top, bottom = top, left = right) => {
  return zoomMultiple(top, right, bottom, left);
};

export const zoomMargin = (top, right = top, bottom = top, left = right) => {
  return zoomMultiple(top, right, bottom, left);
};

export const zoomBorder = (width, style = 'solid', color = 'transparent') => {
  return `${zoom(width)} ${style} ${color}`;
};

export const zoomBoxShadow = (shadows) => {
  if (Array.isArray(shadows)) {
    return shadows.map(shadow => {
      // Parse shadow values and zoom the numeric ones
      return shadow.replace(/(-?\d*\.?\d+)(px|em|rem)/g, (match, number, unit) => {
        return `${parseFloat(number) * getZoomFactor()}${unit}`;
      });
    }).join(', ');
  }
  
  return shadows.replace(/(-?\d*\.?\d+)(px|em|rem)/g, (match, number, unit) => {
    return `${parseFloat(number) * getZoomFactor()}${unit}`;
  });
};

// Get responsive zoom factor based on current viewport
export const getResponsiveZoom = () => {
  const factor = getZoomFactor();
  const isMobile = window.innerWidth <= 767;
  
  return {
    factor,
    isMobile,
    isLarge: factor > 1.0,
    is4K: factor >= 2.0
  };
};